import { Component, OnInit } from "@angular/core";
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { AuthService } from "../auth.service";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { LANG } from "../../app.component";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
  standalone: false,
})
export class LoginComponent implements OnInit {
  loginForm = new UntypedFormGroup({
    email: new UntypedFormControl("", [
      (c) => Validators.required(c),
      (c) => Validators.email(c),
    ]),
  });

  sending = false;
  redirecting = false;
  email = "";
  successMessage: string | null = null;
  errorMessage: string | null = null;
  private language: string;
  private readonly supportedLanguages = ["en", "es", "pl"];

  constructor(
    private auth: AuthService,
    private router: Router,
    private translate: TranslateService,
  ) {
    this.translate.addLangs(this.supportedLanguages);

    const appLanguage = localStorage.getItem(LANG);
    const browserLanguage = this.resolveBrowserLanguage();
    const selectedLanguage =
      appLanguage && this.supportedLanguages.includes(appLanguage)
        ? appLanguage
        : browserLanguage;

    this.translate.setDefaultLang(selectedLanguage);
    this.translate.use(selectedLanguage);
    this.language = selectedLanguage;
  }

  ngOnInit(): void {
    const sub = this.auth.user$.subscribe((firebaseUser) => {
      if (firebaseUser?.uid) {
        void this.router
          .navigate(["/login/callback"])
          .finally(() => sub.unsubscribe());
      } else {
        sub.unsubscribe();
      }
    });
  }

  async loginWithGoogle(): Promise<void> {
    this.successMessage = null;
    this.errorMessage = null;
    this.redirecting = true;
    await this.auth.loginWithGoogle().finally(() => (this.redirecting = false));
  }

  async loginWithMicrosoft(): Promise<void> {
    this.successMessage = null;
    this.errorMessage = null;
    this.redirecting = true;
    await this.auth
      .loginWithMicrosoft()
      .finally(() => (this.redirecting = false));
  }

  async sendEmailLink(): Promise<void> {
    if (!this.email) {
      return;
    }

    this.successMessage = null;
    this.errorMessage = null;
    this.sending = true;
    try {
      await this.auth.sendSignWithEmail(this.email, this.language);
      this.successMessage = this.translate.instant(
        "login.emailSentSuccess",
      ) as string;
    } catch {
      this.errorMessage = this.translate.instant(
        "login.emailSentError",
      ) as string;
    } finally {
      this.sending = false;
    }
  }

  private resolveBrowserLanguage(): string {
    const browserLanguage =
      this.translate.getBrowserCultureLang() ||
      this.translate.getBrowserLang() ||
      "";
    const normalizedLanguage = browserLanguage.toLowerCase();

    const exactMatch = this.supportedLanguages.find(
      (language) => language === normalizedLanguage,
    );
    if (exactMatch) {
      return exactMatch;
    }

    const prefixMatch = this.supportedLanguages.find((language) =>
      normalizedLanguage.startsWith(`${language}-`),
    );
    if (prefixMatch) {
      return prefixMatch;
    }

    return this.supportedLanguages[0];
  }
}
